import React from 'react';
import { CircularProgress } from '@heroui/react';

import { store, useAppSelector } from '@/app/store';

import Button from '@/components/Common/Button';
import DigitInput from '@/components/Common/DigitInput';
import FlexRow from '@/components/Common/FlexRow';
import Input from '@/components/Common/Input';


type Phase = 'loading' | 'error' | 'qr' | 'enroll' | 'recovery' | 'confirm';

interface EnrollTwoFAProps {

  onComplete?: () => void;
  completeLabel?: string;

  onCancel?: () => void;
}

const EnrollTwoFA: React.FC<EnrollTwoFAProps> = ({ onComplete, completeLabel, onCancel }) => {
  const login = useAppSelector((state) => state.logins.login);

  const [phase, setPhase] = React.useState<Phase>('loading');
  const [setupToken, setSetupToken] = React.useState<string>('');
  const [expiresAt, setExpiresAt] = React.useState<string>('');
  const [qrPng, setQrPng] = React.useState<string>('');
  const [manualKey, setManualKey] = React.useState<string>('');

  const [deviceName, setDeviceName] = React.useState<string>('');
  const [code, setCode] = React.useState<string>('');
  const [verifying, setVerifying] = React.useState<boolean>(false);
  const [verifyError, setVerifyError] = React.useState<string>('');

  const [recoveryCodes, setRecoveryCodes] = React.useState<string[]>([]);
  const [enrolledDeviceName, setEnrolledDeviceName] = React.useState<string>('');
  const [copied, setCopied] = React.useState<boolean>(false);

  const runStart = React.useCallback(async (isCancelled?: () => boolean) => {
    setPhase('loading');
    const r = await store.startEnrollment();
    if (isCancelled && isCancelled()) return;
    if (!r) {
      setPhase('error');
      return;
    }
    setSetupToken(r.setup_token);
    setExpiresAt(r.expires_at);
    setQrPng(r.qr_png_base64);
    setManualKey(r.manual_entry_key);
    setPhase('qr');
    sessionStorage.setItem('enrollTwoFA_session', JSON.stringify({
      setup_token: r.setup_token, expires_at: r.expires_at, phase: 'qr',
    }));
  }, []);

  // Refresh-on-QR → log out. Refresh-after-Next → reuse saved setup_token.
  React.useEffect(() => {
    const KEY = 'enrollTwoFA_session';
    const raw = sessionStorage.getItem(KEY);
    if (raw) {
      try {
        const s = JSON.parse(raw);
        const valid = s.expires_at && new Date(s.expires_at).getTime() > Date.now();
        if (valid && s.phase === 'enroll' && s.setup_token) {
          setSetupToken(s.setup_token);
          setExpiresAt(s.expires_at);
          setPhase('enroll');
          return;
        }
      } catch (_e) { /* fall through */ }
      sessionStorage.removeItem(KEY);

      if (!onComplete) {
        store.performLogout();
        return;
      }

    }

    let cancelled = false;
    runStart(() => cancelled);
    return () => { cancelled = true; };
  }, [runStart]);

  const codeInvalid = React.useMemo(() => !/^\d{6}$/.test(code.trim()), [code]);

  const handleVerify = React.useCallback(async () => {
    setVerifyError('');
    if (!deviceName.trim()) {
      setVerifyError('Device name is required.');
      return;
    }
    if (codeInvalid) {
      setVerifyError('Enter the 6-digit code from your authenticator.');
      return;
    }
    setVerifying(true);
    try {
      const resp = await store.verifyEnrollment(setupToken, code.trim(), deviceName.trim());
      if (!resp || !resp.device_id) {
        setCode('');
        setVerifyError('That code didn\'t match. Try again with a fresh code from your app.');
        return;
      }
      setEnrolledDeviceName(resp.device_name);
      sessionStorage.removeItem('enrollTwoFA_session');
      if (resp.recovery_codes && resp.recovery_codes.length > 0) {
        // First-device path: show recovery codes BEFORE confirmation.
        setRecoveryCodes(resp.recovery_codes);
        setPhase('recovery');
      } else {
        // Add-device path: no recovery codes to show, jump straight to confirm.
        setPhase('confirm');
      }
    } finally {
      setVerifying(false);
    }
  }, [code, codeInvalid, setupToken, deviceName]);

  const handleCopyCodes = React.useCallback(async () => {
    await navigator.clipboard.writeText(recoveryCodes.join('\n'));
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  }, [recoveryCodes]);

  const finalCTALabel = completeLabel || 'Log out and sign in again';
  const handleFinalCTA = React.useCallback(async () => {
    await store.refreshLoginDescriptor();
    if (onComplete) {
      onComplete();
      return;
    }
    store.performLogout();
  }, [onComplete]);

  const isEmbedded = !!onComplete;

  const rootClass = isEmbedded
    ? 'w-full py-[24px] bg-[--Light-Grey] min-h-full'
    : 'fixed inset-0 bg-[--Light-Grey] overflow-y-auto py-[24px]';

  if (phase === 'enroll') {
    return (
    <div className={rootClass + ' flex flex-col'}>
      <div className="
             w-full
             max-w-[760px]
             mx-auto
             my-auto
             loginBanner
             rounded-[4px]
             overflow-hidden
             flex
             flex-row
             items-stretch
             bg-white">
        {/* Left panel — branding + reassurance */}
        <div className="bg-[--Primetime-main] text-white p-[28px] flex flex-col gap-[14px] w-[300px] flex-shrink-0">
          <img src="/assets/img/logo_lg.png"
               className="w-[140px]" />
          <div className="bg-white/15 rounded-[8px] w-[44px] h-[44px] flex items-center justify-center mt-[24px]">
            <span className="material-icons !text-white text-[24px]">lock</span>
          </div>
          <h2 className="text-[20px] font-bold m-0 mt-[8px]">
            Verify it's you
          </h2>
          <p className="text-[13px] opacity-90 m-0 leading-[18px]">
            An additional security step keeps your Primetime account safe — even if your password is compromised.
          </p>
        </div>
        {/* Right panel — code entry */}
        <div className="bg-white p-[28px] flex flex-col gap-[14px] flex-1 min-w-0">
          <p className="text-[12px] text-[--Dark-Grey] m-0">
            Signed in as <strong>{login?.username}</strong>
          </p>
          <h1 className="text-[20px] font-bold m-0">
            Enter your authentication code
          </h1>
          <p className="text-[13px] text-[--Dark-Grey] m-0 leading-[18px]">
            Open the authenticator app on your phone and enter the 6-digit code shown.
          </p>
          <div className="py-[12px]">
            <DigitInput value={code}
                        onChange={setCode}
                        disabled={verifying}
                        autoFocus />
          </div>
          { verifyError &&
            <p className="text-[12px] text-[--Error] m-0 text-center">{ verifyError }</p>
          }
          <Input value={deviceName}
                 label="Device name*"
                 labelPlacement="outside"
                 onValueChange={setDeviceName}
                 type="text"
                 placeholder="iPhone, 1Password, Office laptop, ..."
                 isInvalid={verifyError === 'Device name is required.'}
                 classNames={{
                   inputWrapper: "px-[12px] py-[6px] rounded-[4px] border-solid border-1 border-[--Mid-Grey]",
                   label: "text-[12px] !text-[--Dark-Grey]",
                 }} />
          <div className={`flex flex-row ${qrPng ? 'justify-between' : 'justify-end'} items-center mt-[4px]`}>
            { qrPng &&
              <span onClick={() => {
                      const KEY = 'enrollTwoFA_session';
                      const raw = sessionStorage.getItem(KEY);
                      if (raw) {
                        try {
                          const s = JSON.parse(raw);
                          s.phase = 'qr';
                          sessionStorage.setItem(KEY, JSON.stringify(s));
                        } catch (_e) { /* ignore */ }
                      }
                      setPhase('qr');
                    }}
                    className="text-[--Dark-Grey] text-[13px] cursor-pointer hover:underline">
                ← Back to QR code
              </span>
            }
            <Button caption={verifying ? "Verifying…" : "Verify and continue"}
                    layout={verifying ? 'warning_bg' : 'default'}
                    isDisabled={verifying}
                    onClick={handleVerify} />
          </div>
        </div>
      </div>
    </div>
    );
  }

  return (
  <FlexRow direction="column"
           className={rootClass}>
    <FlexRow direction="column"
             flex={0}
             className="
               w-full
               max-w-[480px]
               m-auto
               loginBanner">
      <FlexRow className="bg-[--Primetime-main]"
               justify="center"
               flex={0}>
        <img src="/assets/img/logo_lg.png"
             className="w-[140px]" />
      </FlexRow>
      <FlexRow direction="column"
               justify="center"
               className="px-[24px] py-[16px] gap-[10px] bg-white">
        <h1 className="r14 text-black font-bold text-center">
          { phase === 'recovery'
              ? 'Save Your Recovery Codes'
              : 'Set Up Two-Factor Authentication' }
        </h1>
        { phase === 'qr' &&
          <p className="text-[--Dark-Grey] text-[13px] text-center m-0">
            Scan this QR code with your authenticator app
          </p>
        }
        { phase === 'recovery' &&
          <p className="text-[--Dark-Grey] text-[12px] text-center m-0">
            Store these somewhere safe. Each code can be used once if you lose access to your authenticator.
          </p>
        }
        { phase !== 'qr' && phase !== 'recovery' &&
          <p className="text-[--Dark-Grey] text-[13px]">
            One-time setup for <strong>{login?.username}</strong>.
          </p>
        }

        { phase === 'loading' &&
          <FlexRow justify="center" className="py-[32px]">
            <CircularProgress aria-label="loading" />
          </FlexRow>
        }

        { phase === 'error' &&
          <>
            <p className="text-[13px] text-[--Error] text-center m-0 leading-[18px]">
              We couldn't start two-factor setup. Your session may have expired, or the server is temporarily unavailable.
            </p>
            <Button caption="Try again"
                    width="w-full"
                    onClick={() => runStart()} />
            { onCancel &&
              <FlexRow justify="center"
                       className="text-[--Dark-Grey] text-[13px] p-[4px] cursor-pointer hover:underline">
                <span onClick={onCancel}>Back</span>
              </FlexRow>
            }
          </>
        }

        { phase === 'qr' &&
          <>
            <FlexRow flex={0}
                     className="border border-[--Mid-Grey] rounded-[4px] p-[12px] gap-[12px] items-center">
              <img src={`data:image/png;base64,${qrPng}`}
                   alt="2FA QR code"
                   className="w-[160px] h-[160px] flex-shrink-0" />
              <FlexRow direction="column" className="gap-[8px]">
                <FlexRow flex={0} className="gap-[8px] items-start">
                  <div className="bg-[--Primetime-main] text-white rounded-full w-[20px] h-[20px] flex items-center justify-center text-[11px] font-bold flex-shrink-0">
                    1
                  </div>
                  <span className="text-[12px] leading-[20px]">
                    Open your authenticator app
                  </span>
                </FlexRow>
                <FlexRow flex={0} className="gap-[8px] items-start">
                  <div className="bg-[--Primetime-main] text-white rounded-full w-[20px] h-[20px] flex items-center justify-center text-[11px] font-bold flex-shrink-0">
                    2
                  </div>
                  <span className="text-[12px] leading-[16px]">
                    Tap "+" and select <em>Scan QR code</em>
                  </span>
                </FlexRow>
                <FlexRow flex={0} className="gap-[8px] items-start">
                  <div className="bg-[--Primetime-main] text-white rounded-full w-[20px] h-[20px] flex items-center justify-center text-[11px] font-bold flex-shrink-0">
                    3
                  </div>
                  <span className="text-[12px] leading-[16px]">
                    Enter the 6-digit code on the next screen
                  </span>
                </FlexRow>
              </FlexRow>
            </FlexRow>
            <p className="text-[11px] text-[--Dark-Grey] m-0">
              Can't scan? Enter this key manually:
            </p>
            <div className="font-mono text-[11px] bg-[--Light-Grey] p-[6px] rounded-[4px] select-all break-all">
              { manualKey }
            </div>
            <Button caption="Next"
                    width="w-full"
                    onClick={() => {
                      sessionStorage.setItem('enrollTwoFA_session', JSON.stringify({
                        setup_token: setupToken, expires_at: expiresAt, phase: 'enroll',
                      }));
                      setPhase('enroll');
                    }} />
            { onCancel &&
              <FlexRow justify="center"
                       className="text-[--Dark-Grey] text-[13px] p-[4px] cursor-pointer hover:underline">
                <span onClick={onCancel}>Cancel</span>
              </FlexRow>
            }
          </>
        }

        { phase === 'recovery' &&
          <>
            <div className="border border-[--Mid-Grey] rounded-[4px] p-[16px] grid grid-cols-2 gap-x-[24px] gap-y-[8px] font-mono text-[12px] mt-[8px]">
              { recoveryCodes.map((c) => (
                <div key={c} className="select-all">{ c }</div>
              )) }
            </div>
            <div className="flex flex-row gap-[12px] mt-[4px]">
              <Button caption={copied ? "Copied!" : "Copy all"}
                      width="flex-1"
                      layout="white"
                      onClick={handleCopyCodes} />
              <Button caption="I've saved my codes"
                      width="flex-1"
                      onClick={() => setPhase('confirm')} />
            </div>
          </>
        }

        { phase === 'confirm' &&
          <>
            <p className="text-[13px] text-[--Primetime-main] font-bold m-0">
              { enrolledDeviceName
                ? `"${enrolledDeviceName}" is now active on your account.`
                : '2FA is now active on your account.' }
            </p>
            { !onComplete &&
              <p className="text-[12px] text-[--Dark-Grey] m-0">
                The next time you sign in, you'll be asked for your password AND a code from
                your authenticator app. Log out now to verify the flow end-to-end.
              </p>
            }
            <Button caption={finalCTALabel}
                    width="w-full"
                    onClick={handleFinalCTA} />
          </>
        }
      </FlexRow>
      <FlexRow flex={0}
               direction="column"
               className="pb-[32px] bg-white">
      </FlexRow>
    </FlexRow>
  </FlexRow>
  );
};

export default EnrollTwoFA;
